import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { FileText, Download, RefreshCw, Users, Calendar, DollarSign, Search, Eye, Printer } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function PayslipManagement({ user, onLogout }) {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [payslip, setPayslip] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [monthPayrolls, setMonthPayrolls] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (month && year) {
      fetchMonthPayrolls();
    }
  }, [month, year]);

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchMonthPayrolls = async () => {
    setDataLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/payroll/month/${month}/year/${year}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setMonthPayrolls(response.data.payroll_records || []);
    } catch (error) {
      console.error('Error fetching month payrolls:', error);
      setMonthPayrolls([]);
    } finally {
      setDataLoading(false);
    }
  };

  const handleGeneratePayslip = async () => {
    if (!selectedEmployee) {
      alert('Please select an employee');
      return;
    }

    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/payroll/generate-advanced`,
        {
          employee_id: selectedEmployee,
          month: parseInt(month),
          year: parseInt(year)
        },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setPayslip(response.data.payroll);
      alert('✅ Payslip generated successfully!');
      fetchMonthPayrolls();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGenerating(false);
    }
  };

  const handleBulkGenerate = async () => {
    if (!window.confirm(`Generate payroll for all employees for ${getMonthName(month)} ${year}?`)) {
      return;
    }

    setBulkGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/payroll/bulk-generate?month=${month}&year=${year}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      alert(`✅ Bulk generation complete! Generated: ${response.data.generated}, Failed: ${response.data.failed}`);
      fetchMonthPayrolls();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setBulkGenerating(false);
    }
  };

  const handleViewPayslip = async (employeeId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/payroll/employee/${employeeId}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      const records = response.data.payroll_records || [];
      const record = records.find(r => r.month === parseInt(month) && r.year === parseInt(year));
      if (record) {
        setPayslip(record);
      } else {
        alert('No payslip found for this month');
      }
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = (payslipData = payslip) => {
    if (!payslipData) return;
    
    // Generate HTML content for PDF
    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Payslip - ${payslipData.employee_name} - ${getMonthName(payslipData.month)} ${payslipData.year}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; background: #fff; color: #333; }
    .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #2b3380; padding-bottom: 20px; }
    .company-name { font-size: 28px; font-weight: bold; color: #2b3380; }
    .company-address { font-size: 12px; color: #666; margin-top: 5px; }
    .payslip-title { font-size: 20px; margin-top: 15px; color: #333; text-transform: uppercase; letter-spacing: 2px; }
    .payslip-period { font-size: 14px; color: #666; margin-top: 5px; }
    .employee-info { display: flex; justify-content: space-between; margin-bottom: 30px; background: #f8f9fa; padding: 20px; border-radius: 8px; }
    .info-group { }
    .info-label { font-size: 11px; color: #666; text-transform: uppercase; }
    .info-value { font-size: 14px; font-weight: 600; color: #333; margin-top: 2px; }
    .section { margin-bottom: 25px; }
    .section-title { font-size: 14px; font-weight: bold; color: #2b3380; margin-bottom: 10px; text-transform: uppercase; border-bottom: 1px solid #e0e0e0; padding-bottom: 5px; }
    table { width: 100%; border-collapse: collapse; }
    th, td { padding: 10px 15px; text-align: left; border-bottom: 1px solid #eee; }
    th { background: #f5f7fa; font-size: 12px; color: #666; text-transform: uppercase; }
    td { font-size: 13px; }
    .amount { text-align: right; font-family: 'Courier New', monospace; }
    .total-row { background: #f0f4ff; font-weight: bold; }
    .net-salary { background: #2b3380; color: white; }
    .net-salary td { font-size: 16px; padding: 15px; }
    .attendance-box { display: flex; justify-content: space-around; background: #f8f9fa; padding: 15px; border-radius: 8px; margin-top: 10px; }
    .att-item { text-align: center; }
    .att-value { font-size: 24px; font-weight: bold; color: #2b3380; }
    .att-label { font-size: 11px; color: #666; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #eee; font-size: 11px; color: #999; text-align: center; }
    @media print { body { padding: 20px; } }
  </style>
</head>
<body>
  <div class="header">
    <div class="company-name">ZOBOX PROTECH LIMITED</div>
    <div class="company-address">Corporate Office: Delhi NCR | www.zobox.in</div>
    <div class="payslip-title">Salary Slip</div>
    <div class="payslip-period">${getMonthName(payslipData.month)} ${payslipData.year}</div>
  </div>

  <div class="employee-info">
    <div class="info-group">
      <div class="info-label">Employee Name</div>
      <div class="info-value">${payslipData.employee_name || 'N/A'}</div>
    </div>
    <div class="info-group">
      <div class="info-label">Employee ID</div>
      <div class="info-value">${payslipData.employee_id || 'N/A'}</div>
    </div>
    <div class="info-group">
      <div class="info-label">Department</div>
      <div class="info-value">${payslipData.department || 'N/A'}</div>
    </div>
    <div class="info-group">
      <div class="info-label">Designation</div>
      <div class="info-value">${payslipData.designation || 'N/A'}</div>
    </div>
  </div>

  <div style="display: flex; gap: 20px;">
    <div class="section" style="flex: 1;">
      <div class="section-title">Earnings</div>
      <table>
        <tr><td>Basic Salary</td><td class="amount">₹${formatCurrency(payslipData.earnings?.basic)}</td></tr>
        <tr><td>House Rent Allowance (HRA)</td><td class="amount">₹${formatCurrency(payslipData.earnings?.hra)}</td></tr>
        <tr><td>Dearness Allowance (DA)</td><td class="amount">₹${formatCurrency(payslipData.earnings?.da)}</td></tr>
        <tr><td>Special Allowance</td><td class="amount">₹${formatCurrency(payslipData.earnings?.special_allowance)}</td></tr>
        <tr><td>Conveyance Allowance</td><td class="amount">₹${formatCurrency(payslipData.earnings?.conveyance)}</td></tr>
        <tr><td>Medical Allowance</td><td class="amount">₹${formatCurrency(payslipData.earnings?.medical)}</td></tr>
        <tr><td>Travel Reimbursement</td><td class="amount">₹${formatCurrency(payslipData.earnings?.travel_reimbursement)}</td></tr>
        <tr class="total-row"><td><strong>Gross Earnings</strong></td><td class="amount"><strong>₹${formatCurrency(payslipData.gross_salary)}</strong></td></tr>
      </table>
    </div>

    <div class="section" style="flex: 1;">
      <div class="section-title">Deductions</div>
      <table>
        <tr><td>Provident Fund (PF)</td><td class="amount">₹${formatCurrency(payslipData.deductions?.pf_employee)}</td></tr>
        <tr><td>ESI</td><td class="amount">₹${formatCurrency(payslipData.deductions?.esi)}</td></tr>
        <tr><td>Professional Tax</td><td class="amount">₹${formatCurrency(payslipData.deductions?.prof_tax)}</td></tr>
        <tr><td>TDS</td><td class="amount">₹${formatCurrency(payslipData.deductions?.tds)}</td></tr>
        <tr><td>Late Penalty</td><td class="amount">₹${formatCurrency(payslipData.deductions?.late_penalty)}</td></tr>
        <tr><td>LOP Deduction</td><td class="amount">₹${formatCurrency(payslipData.deductions?.lop_deduction)}</td></tr>
        <tr><td>Half Day Deduction</td><td class="amount">₹${formatCurrency(payslipData.deductions?.half_day_deduction)}</td></tr>
        <tr class="total-row"><td><strong>Total Deductions</strong></td><td class="amount"><strong>₹${formatCurrency(payslipData.total_deductions)}</strong></td></tr>
      </table>
    </div>
  </div>

  <table style="margin-top: 20px;">
    <tr class="net-salary">
      <td><strong>NET SALARY PAYABLE</strong></td>
      <td class="amount"><strong>₹${formatCurrency(payslipData.net_salary)}</strong></td>
    </tr>
  </table>

  <div class="section" style="margin-top: 30px;">
    <div class="section-title">Attendance Summary</div>
    <div class="attendance-box">
      <div class="att-item">
        <div class="att-value">${payslipData.attendance?.present_days || 0}</div>
        <div class="att-label">Present Days</div>
      </div>
      <div class="att-item">
        <div class="att-value">${payslipData.attendance?.lop_days || 0}</div>
        <div class="att-label">LOP Days</div>
      </div>
      <div class="att-item">
        <div class="att-value">${payslipData.attendance?.late_comings || 0}</div>
        <div class="att-label">Late Comings</div>
      </div>
      <div class="att-item">
        <div class="att-value">${payslipData.attendance?.half_days || 0}</div>
        <div class="att-label">Half Days</div>
      </div>
    </div>
  </div>

  <div class="footer">
    <p>This is a computer-generated payslip and does not require a signature.</p>
    <p>Generated on: ${new Date().toLocaleString('en-IN')}</p>
  </div>
</body>
</html>`;

    // Create a new window and print
    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // Wait for content to load then trigger print
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getMonthName = (monthNum) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return months[monthNum - 1];
  };

  const filteredPayrolls = monthPayrolls.filter(p => 
    p.employee_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.employee_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const canManage = user?.role?.toLowerCase() === 'admin' || user?.role?.toLowerCase() === 'hr' || user?.role?.toLowerCase() === 'hr manager';

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>💰 Payslip Management</h1>
            <p className="mt-2 text-gray-600">Generate, view, and download employee payslips</p>
          </div>
          <button
            onClick={fetchMonthPayrolls}
            disabled={dataLoading}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${dataLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full" style={{backgroundColor: '#e8eaf6'}}>
              <Users className="w-6 h-6" style={{color: '#2b3380'}} />
            </div>
            <div>
              <p className="text-2xl font-bold" style={{color: '#2b3380'}}>{monthPayrolls.length}</p>
              <p className="text-sm text-gray-500">Payslips Generated</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-green-100">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">
                ₹{formatCurrency(monthPayrolls.reduce((sum, p) => sum + (p.net_salary || 0), 0))}
              </p>
              <p className="text-sm text-gray-500">Total Net Salary</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-blue-100">
              <Calendar className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-600">{getMonthName(month)}</p>
              <p className="text-sm text-gray-500">{year}</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-orange-100">
              <FileText className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-orange-600">{employees.length}</p>
              <p className="text-sm text-gray-500">Total Employees</p>
            </div>
          </div>
        </div>

        {/* Controls */}
        {canManage && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-lg font-bold mb-4" style={{color: '#2b3380'}}>Generate Payslips</h2>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Month</label>
                <select
                  value={month}
                  onChange={(e) => setMonth(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                    <option key={m} value={m}>{getMonthName(m)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
                <select
                  value={year}
                  onChange={(e) => setYear(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {[2024, 2025, 2026].map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Employee</label>
                <select
                  value={selectedEmployee}
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Employee</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.name} ({emp.id})</option>
                  ))}
                </select>
              </div>
              <button
                onClick={handleGeneratePayslip}
                disabled={generating || !selectedEmployee}
                className="px-4 py-2 text-white rounded-lg flex items-center justify-center gap-2"
                style={{backgroundColor: generating || !selectedEmployee ? '#9e9e9e' : '#2b3380'}}
              >
                <FileText className="w-4 h-4" />
                {generating ? 'Generating...' : 'Generate'}
              </button>
              <button
                onClick={handleBulkGenerate}
                disabled={bulkGenerating}
                className="px-4 py-2 bg-green-600 text-white rounded-lg flex items-center justify-center gap-2 hover:bg-green-700"
              >
                <Users className="w-4 h-4" />
                {bulkGenerating ? 'Processing...' : 'Bulk Generate All'}
              </button>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by employee name or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Payslips Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b" style={{backgroundColor: '#f5f7fa'}}>
            <h2 className="text-lg font-bold" style={{color: '#2b3380'}}>
              Payslips for {getMonthName(month)} {year}
            </h2>
          </div>
          
          {dataLoading ? (
            <div className="p-12 text-center">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
              <p className="mt-3 text-gray-500">Loading payslips...</p>
            </div>
          ) : filteredPayrolls.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-lg font-medium text-gray-500 mb-2">No payslips found</p>
              <p className="text-gray-400">
                {searchTerm ? 'Try different search terms' : 'Generate payslips for this month'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead style={{backgroundColor: '#2b3380'}}>
                  <tr>
                    <th className="px-6 py-3 text-left text-white text-sm">Employee</th>
                    <th className="px-6 py-3 text-left text-white text-sm">Employee ID</th>
                    <th className="px-6 py-3 text-right text-white text-sm">Gross Salary</th>
                    <th className="px-6 py-3 text-right text-white text-sm">Deductions</th>
                    <th className="px-6 py-3 text-right text-white text-sm">Net Salary</th>
                    <th className="px-6 py-3 text-center text-white text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPayrolls.map((record, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">{record.employee_name}</td>
                      <td className="px-6 py-4 text-gray-600">{record.employee_id}</td>
                      <td className="px-6 py-4 text-right font-mono">₹{formatCurrency(record.gross_salary)}</td>
                      <td className="px-6 py-4 text-right font-mono text-red-600">₹{formatCurrency(record.total_deductions)}</td>
                      <td className="px-6 py-4 text-right font-mono font-bold text-green-600">₹{formatCurrency(record.net_salary)}</td>
                      <td className="px-6 py-4">
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => setPayslip(record)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDownloadPDF(record)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                            title="Download/Print PDF"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Payslip Detail Modal */}
        {payslip && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-2xl font-bold" style={{color: '#2b3380'}}>Payslip Details</h2>
                    <p className="text-gray-600">{getMonthName(payslip.month)} {payslip.year}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleDownloadPDF(payslip)}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg flex items-center gap-2 hover:bg-green-700"
                    >
                      <Printer className="w-4 h-4" />
                      Print / Download PDF
                    </button>
                    <button
                      onClick={() => setPayslip(null)}
                      className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                    >
                      Close
                    </button>
                  </div>
                </div>

                {/* Employee Info */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Employee Name</p>
                      <p className="font-bold">{payslip.employee_name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Employee ID</p>
                      <p className="font-bold">{payslip.employee_id}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Department</p>
                      <p className="font-bold">{payslip.department || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Designation</p>
                      <p className="font-bold">{payslip.designation || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Earnings and Deductions */}
                <div className="grid grid-cols-2 gap-6 mb-6">
                  <div>
                    <h3 className="font-bold text-green-700 mb-3 flex items-center gap-2">
                      <DollarSign className="w-5 h-5" />
                      Earnings
                    </h3>
                    <div className="space-y-2">
                      <div className="flex justify-between py-2 border-b">
                        <span>Basic</span>
                        <span className="font-mono">₹{formatCurrency(payslip.earnings?.basic)}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span>HRA</span>
                        <span className="font-mono">₹{formatCurrency(payslip.earnings?.hra)}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span>DA</span>
                        <span className="font-mono">₹{formatCurrency(payslip.earnings?.da)}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span>Special Allowance</span>
                        <span className="font-mono">₹{formatCurrency(payslip.earnings?.special_allowance)}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span>Conveyance</span>
                        <span className="font-mono">₹{formatCurrency(payslip.earnings?.conveyance)}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span>Medical</span>
                        <span className="font-mono">₹{formatCurrency(payslip.earnings?.medical)}</span>
                      </div>
                      <div className="flex justify-between py-2 font-bold bg-green-50 px-2 rounded">
                        <span>Gross Salary</span>
                        <span className="font-mono">₹{formatCurrency(payslip.gross_salary)}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-bold text-red-700 mb-3">Deductions</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between py-2 border-b">
                        <span>PF (Employee)</span>
                        <span className="font-mono">₹{formatCurrency(payslip.deductions?.pf_employee)}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span>ESI</span>
                        <span className="font-mono">₹{formatCurrency(payslip.deductions?.esi)}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span>Professional Tax</span>
                        <span className="font-mono">₹{formatCurrency(payslip.deductions?.prof_tax)}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span>TDS</span>
                        <span className="font-mono">₹{formatCurrency(payslip.deductions?.tds)}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span>Late Penalty</span>
                        <span className="font-mono">₹{formatCurrency(payslip.deductions?.late_penalty)}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span>LOP Deduction</span>
                        <span className="font-mono">₹{formatCurrency(payslip.deductions?.lop_deduction)}</span>
                      </div>
                      <div className="flex justify-between py-2 font-bold bg-red-50 px-2 rounded">
                        <span>Total Deductions</span>
                        <span className="font-mono">₹{formatCurrency(payslip.total_deductions)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Net Salary */}
                <div className="p-4 rounded-lg text-white text-center" style={{backgroundColor: '#2b3380'}}>
                  <p className="text-sm opacity-80">Net Salary Payable</p>
                  <p className="text-3xl font-bold">₹{formatCurrency(payslip.net_salary)}</p>
                </div>

                {/* Attendance */}
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-bold mb-3" style={{color: '#2b3380'}}>Attendance Summary</h3>
                  <div className="grid grid-cols-4 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold" style={{color: '#2b3380'}}>{payslip.attendance?.present_days || 0}</p>
                      <p className="text-xs text-gray-500">Present Days</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-red-600">{payslip.attendance?.lop_days || 0}</p>
                      <p className="text-xs text-gray-500">LOP Days</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-orange-600">{payslip.attendance?.late_comings || 0}</p>
                      <p className="text-xs text-gray-500">Late Comings</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-yellow-600">{payslip.attendance?.half_days || 0}</p>
                      <p className="text-xs text-gray-500">Half Days</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default PayslipManagement;
